import { createApi } from "@reduxjs/toolkit/query/react";
import { DynamicBaseQuery } from "./baseQuery";
import { CalendarAppointmentsIndexResponse } from "./types/calendar/responses/CalendarAppointmentsIndexResponse";
import { CalendarAppointmentsFilterRequest } from "./types/calendar/requests/CalendarAppointmentsFilterRequest";
import { EventCreatedResponse } from "./types/calendar/responses/EventCreatedResponse";
import { CreateCalendarEventRequest } from "./types/calendar/requests/CreateCalendarEventRequest";
import { CalendarAppointmentShowResponse } from "./types/calendar/responses/CalendarAppointmentShowResponse";
import { CalendarAppointmentShowRequest } from "./types/calendar/requests/CalendarAppointmentShowRequest";
import { SuccessResponse } from "./types/common/responses/SuccessResponse";
import { CalendarAppointmentStatusUpdateReuqest } from "./types/calendar/requests/CalendarAppointmentStatusUpdateReuqest";
import { shiftHandoverApi } from "./shiftHandoverApi";
import { CalendarEventTypesResponse } from "./types/calendar/responses/CalendarEventTypesResponse";

export const calendarApi = createApi({
  reducerPath: "calendarApi",
  baseQuery: DynamicBaseQuery,
  tagTypes: ["CurrentEvents", "ShowEvent"],
  endpoints: (builder) => ({
    getAllAppointments: builder.query<
      CalendarAppointmentsIndexResponse,
      CalendarAppointmentsFilterRequest
    >({
      query: (params) => ({
        url: "/calendar-events",
        method: "GET",
        params,
      }),
      providesTags: (result) => {
        return (
          result?.data.map((item) => ({
            type: "CurrentEvents",
            id: `${item.id}`,
          })) ?? []
        );
      },
    }),
    getAppointment: builder.query<
      CalendarAppointmentShowResponse,
      CalendarAppointmentShowRequest
    >({
      query: (params) => ({
        url: `/calendar-events/${params.id}`,
        method: "GET",
      }),
      providesTags: (_rsult, _error, args) => [
        { type: "ShowEvent", id: args.id },
      ],
    }),
    cancelAppointment: builder.mutation<
      SuccessResponse,
      CalendarAppointmentStatusUpdateReuqest
    >({
      query: (payload) => ({
        url: `/calendar-events/${payload.id}/cancel`,
        method: "PATCH",
        body: payload.body,
      }),
      invalidatesTags: (_resul, _error, args) => [
        "CurrentEvents",
        { type: "ShowEvent", id: args.id },
      ],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(
          shiftHandoverApi.util.invalidateTags([
            "ShiftHandoverDashboard",
            "ShiftHandover",
          ]),
        );
      },
    }),
    completeAppointment: builder.mutation<
      SuccessResponse,
      CalendarAppointmentStatusUpdateReuqest
    >({
      query: (payload) => ({
        url: `/calendar-events/${payload.id}/complete`,
        method: "PATCH",
        body: payload.body,
      }),
      invalidatesTags: (_resul, _error, args) => [
        "CurrentEvents",
        { type: "ShowEvent", id: args.id },
      ],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(
          shiftHandoverApi.util.invalidateTags([
            "ShiftHandoverDashboard",
            "ShiftHandover",
          ]),
        );
      },
    }),
    createCalendarEvent: builder.mutation<
      EventCreatedResponse,
      CreateCalendarEventRequest
    >({
      query: (body) => ({
        url: "/calendar-events",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CurrentEvents", "ShowEvent"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(
          shiftHandoverApi.util.invalidateTags([
            "ShiftHandover",
            "ShiftHandoverDashboard",
          ]),
        );
      },
    }),
    updateCalendarEvent: builder.mutation<
      EventCreatedResponse,
      {
        params: CalendarAppointmentShowRequest;
        body: CreateCalendarEventRequest;
      }
    >({
      query: (payload) => ({
        url: `/calendar-events/${payload.params.id}`,
        method: "PUT",
        body: payload.body,
      }),
      invalidatesTags: (_resul, _error, args) => [
        "CurrentEvents",
        { type: "ShowEvent", id: args.params.id },
      ],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(
          shiftHandoverApi.util.invalidateTags([
            "ShiftHandover",
            "ShiftHandoverDashboard",
          ]),
        );
      },
    }),
    getICalEvents: builder.query<string, CalendarAppointmentsFilterRequest>({
      query: (params) => ({
        url: "/calendar-events/ical",
        method: "GET",
        params,
        responseHandler: "text",
        headers: {
          Accept: "*/*",
        },
      }),
      providesTags: ["CurrentEvents"],
    }),
    getCalendarEventTypes: builder.query<CalendarEventTypesResponse, void>({
      query: () => ({
        url: "/calendar-events/types",
        method: "GET",
      }),
    }),
  }),
});
export const {
  useGetAllAppointmentsQuery,
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
  useGetICalEventsQuery,
  useGetAppointmentQuery,
  useCancelAppointmentMutation,
  useCompleteAppointmentMutation,
  useGetCalendarEventTypesQuery,
} = calendarApi;
